import { HStack, Pressable, Text } from 'native-base'
import { useCallback, useEffect, useState } from 'react'

import SmileyAngrySVG from '../assets/SmileyAngrySVG'
import SmileyHappySVG from '../assets/SmileyHappySVG'
import SmileyMehSVG from '../assets/SmileyMehSVG'
import Smiley from '../assets/SmileySVG'
import SmileySadSVG from '../assets/SmileySadSVG'
import { colors } from '../utils/theme/configureTheme'

export enum SmileyRating {
  ANGRY = 'angry',
  SAD = 'sad',
  MEH = 'meh',
  SMILEY = 'smiley',
  HAPPY = 'happy',
}

export const SMILEY_RATING_TO_CSAT_MAP: Record<SmileyRating, number> = {
  [SmileyRating.ANGRY]: 1,
  [SmileyRating.SAD]: 2,
  [SmileyRating.MEH]: 3,
  [SmileyRating.SMILEY]: 4,
  [SmileyRating.HAPPY]: 5,
}

const CSAT_TO_SMILEY_RATING_MAP: Record<number, SmileyRating> = {
  1: SmileyRating.ANGRY,
  2: SmileyRating.SAD,
  3: SmileyRating.MEH,
  4: SmileyRating.SMILEY,
  5: SmileyRating.HAPPY,
}

interface ExperienceRatingSmileyProps {
  onSmileyPress: (csatRating: number) => void
  header?: string
  existingRating?: number
}

/** Smiley-face rating row that lets users select a satisfaction level from 1–5. */
export default function ExperienceRatingSmiley({ onSmileyPress, header, existingRating }: ExperienceRatingSmileyProps) {
  const [selectedSmileyRating, setSelectedSmileyRating] = useState<SmileyRating | null>(
    existingRating ? CSAT_TO_SMILEY_RATING_MAP[existingRating] || null : null
  )

  useEffect(() => {
    if (existingRating) {
      const rating = CSAT_TO_SMILEY_RATING_MAP[existingRating]
      if (rating) {
        setSelectedSmileyRating(rating)
      }
    }
  }, [existingRating])

  const handleSmileyPress = useCallback(
    (rating: SmileyRating) => {
      setSelectedSmileyRating(rating)
      const csatRating = SMILEY_RATING_TO_CSAT_MAP[rating]
      onSmileyPress(csatRating)
    },
    [onSmileyPress]
  )

  const getLineColor = useCallback(
    (rating: SmileyRating) => {
      const isSelected = selectedSmileyRating === rating

      if (isSelected) {
        return '#3B5466'
      } else if (!isSelected && !!selectedSmileyRating) {
        return colors.gray[100]
      } else {
        return '#5D5D5A'
      }
    },
    [selectedSmileyRating]
  )

  const getBackgroundColor = useCallback(
    (rating: SmileyRating) => {
      const isSelected = selectedSmileyRating === rating

      if (isSelected) {
        return colors.brand.lightBlue
      } else {
        return 'transparent'
      }
    },
    [selectedSmileyRating]
  )

  return (
    <>
      <Text
        fontSize="14px"
        fontWeight="500"
        lineHeight={'125%'}
        letterSpacing={'-0.1%'}
        fontFamily="Matter Regular"
        textAlign="center"
        mb={1}
      >
        {header || 'How was your delivery experience?'}
      </Text>
      <HStack space={3} justifyContent="center" alignItems="center">
        <Pressable onPress={() => handleSmileyPress(SmileyRating.ANGRY)}>
          <SmileyAngrySVG
            fillColor={getLineColor(SmileyRating.ANGRY)}
            backgroundColor={getBackgroundColor(SmileyRating.ANGRY)}
          />
        </Pressable>
        <Pressable onPress={() => handleSmileyPress(SmileyRating.SAD)}>
          <SmileySadSVG
            fillColor={getLineColor(SmileyRating.SAD)}
            backgroundColor={getBackgroundColor(SmileyRating.SAD)}
          />
        </Pressable>
        <Pressable onPress={() => handleSmileyPress(SmileyRating.MEH)}>
          <SmileyMehSVG
            fillColor={getLineColor(SmileyRating.MEH)}
            backgroundColor={getBackgroundColor(SmileyRating.MEH)}
          />
        </Pressable>
        <Pressable onPress={() => handleSmileyPress(SmileyRating.SMILEY)}>
          <Smiley
            fillColor={getLineColor(SmileyRating.SMILEY)}
            backgroundColor={getBackgroundColor(SmileyRating.SMILEY)}
          />
        </Pressable>
        <Pressable onPress={() => handleSmileyPress(SmileyRating.HAPPY)}>
          <SmileyHappySVG
            fillColor={getLineColor(SmileyRating.HAPPY)}
            backgroundColor={getBackgroundColor(SmileyRating.HAPPY)}
          />
        </Pressable>
      </HStack>
    </>
  )
}
